import { useState, useEffect, useCallback } from "react";
import DashboardService from "../services/DashboardService";

export interface DashboardData {
  indicators: {
    openTickets: number;
    closedTickets: number;
    pendingTickets: number;
    totalMessages: number;
    uniqueContacts: number;
  };
  topContactsByTickets: Array<{
    contactId: number;
    contactName: string;
    count: number;
  }>;
  topContactsByMessages: Array<{
    contactId: number;
    contactName: string;
    count: number;
  }>;
  timeSeries: Array<{
    period: string;
    ticketsCount: number;
    messagesCount: number;
  }>;
}

interface Filters {
  startDate?: string;
  endDate?: string;
  groupBy?: "day" | "week" | "month";
}

// Dados mockados para testes quando API não está disponível
const MOCK_DATA: DashboardData = {
  indicators: {
    openTickets: 45,
    closedTickets: 128,
    pendingTickets: 23,
    totalMessages: 1250,
    uniqueContacts: 87,
  },
  topContactsByTickets: [
    { contactId: 1, contactName: "João Silva", count: 15 },
    { contactId: 2, contactName: "Maria Santos", count: 12 },
    { contactId: 3, contactName: "Pedro Oliveira", count: 11 },
    { contactId: 4, contactName: "Ana Costa", count: 10 },
    { contactId: 5, contactName: "Carlos Ferreira", count: 9 },
    { contactId: 6, contactName: "Lucia Alves", count: 8 },
    { contactId: 7, contactName: "Bruno Martins", count: 7 },
    { contactId: 8, contactName: "Fernanda Rocha", count: 6 },
    { contactId: 9, contactName: "Gabriel Costa", count: 5 },
    { contactId: 10, contactName: "Helena Silva", count: 4 },
  ],
  topContactsByMessages: [
    { contactId: 1, contactName: "João Silva", count: 125 },
    { contactId: 2, contactName: "Maria Santos", count: 98 },
    { contactId: 3, contactName: "Pedro Oliveira", count: 87 },
    { contactId: 4, contactName: "Ana Costa", count: 76 },
    { contactId: 5, contactName: "Carlos Ferreira", count: 65 },
    { contactId: 6, contactName: "Lucia Alves", count: 54 },
    { contactId: 7, contactName: "Bruno Martins", count: 43 },
    { contactId: 8, contactName: "Fernanda Rocha", count: 32 },
    { contactId: 9, contactName: "Gabriel Costa", count: 21 },
    { contactId: 10, contactName: "Helena Silva", count: 18 },
  ],
  timeSeries: [
    {
      period: "2026-03-19",
      ticketsCount: 5,
      messagesCount: 45,
    },
    {
      period: "2026-03-20",
      ticketsCount: 8,
      messagesCount: 62,
    },
    {
      period: "2026-03-21",
      ticketsCount: 6,
      messagesCount: 58,
    },
    {
      period: "2026-03-22",
      ticketsCount: 10,
      messagesCount: 72,
    },
    {
      period: "2026-03-23",
      ticketsCount: 7,
      messagesCount: 68,
    },
    {
      period: "2026-03-24",
      ticketsCount: 9,
      messagesCount: 85,
    },
    {
      period: "2026-03-25",
      ticketsCount: 12,
      messagesCount: 92,
    },
  ],
};

export const useDashboardData = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<Filters>({
    groupBy: "day",
  });

  // Inicializar com últimos 7 dias
  useEffect(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);

    const start = startDate.toISOString().split("T")[0];
    const end = endDate.toISOString().split("T")[0];

    setFilters({
      startDate: start,
      endDate: end,
      groupBy: "day",
    });
  }, []);

  // Função para buscar dados da API
  /**
   * Busca dados do dashboard na API real.
   * Fallback para MOCK_DATA só ocorre em ambiente de desenvolvimento E se a flag REACT_APP_DASHBOARD_USE_MOCK=true.
   * Nunca faz fallback em produção, mesmo se a flag estiver setada.
   */
  const fetchDashboardData = useCallback(
    async (overrideFilters?: Filters) => {
      setLoading(true);
      setError(null);
      const activeFilters = overrideFilters || filters;
      try {
        const responseData = await DashboardService.getDashboard({
          startDate: activeFilters.startDate,
          endDate: activeFilters.endDate,
          groupBy: activeFilters.groupBy,
        });
        setData(responseData);
      } catch (apiError: any) {
        // Fallback controlado para MOCK_DATA apenas se em dev e flag ativada
        const useMock = process.env.REACT_APP_DASHBOARD_USE_MOCK === "true";
        const isDev = process.env.NODE_ENV === "development";
        if (isDev && useMock) {
          setData(MOCK_DATA);
          setError(null);
        } else {
          setError(
            "Não foi possível carregar os dados do dashboard. Por favor, tente novamente mais tarde ou contate o suporte se o problema persistir.",
          );
          setData(null);
        }
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  // Buscar dados quando os filtros mudam
  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const updateFilters = (newFilters: Filters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  // Refresh realmente refaz a busca na API
  const refreshData = useCallback(async () => {
    await fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    data,
    loading,
    error,
    filters,
    updateFilters,
    refreshData,
  };
};
